import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Users, Settings, CreditCard, Receipt, LogOut, ChevronLeft, Package, Building2, FileSpreadsheet, ScrollText, ClipboardList, Bell, CalendarDays, UserRound } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { isAuthenticated, logout as keycloakLogout } from '@/services/keycloak';
import { Button } from '@/components/ui/button';
import { useMemo } from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { getCompanySettings } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import { User } from '@/types/firebase';
import { Toaster } from '@/components/ui/toaster';
import { Badge } from '@/components/ui/badge';
import { BackgroundPattern } from '@/components/ui/BackgroundPattern';
import TestModeBanner from '@/components/TestModeBanner';
import { useTestMode } from '@/hooks/useTestMode';
import { CompanySettings } from '@/types/firebase';
import { getBrandingConfig, updatePageBranding } from '@/utils/branding';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { useImpersonation } from '@/hooks/useImpersonation';
import { MerchantApplicationService } from '@/features/merchant-application/services/merchantApplicationService';
import { bankingPartnerService } from '@/features/banking-partners/services/bankingPartnerService';

const PIPELINE_COLORS: Record<string, string> = {
  lead: '#6B7280',
  phone: '#3B82F6',
  offer: '#6366F1',
  underwriting: '#F59E0B',
  documents: '#8B5CF6',
  approved: '#10B981',
  declined: '#EF4444',
  onboarded: '#059669',
  failed_submission: '#EC4899',
};

// Define the navigation item type
interface NavigationItem {
  name: string;
  href: string;
  icon: React.ElementType;
  color?: string;
  permissionCheck?: () => boolean;
  subItems?: NavigationItem[];
  notificationCount?: number;
}

// Base navigation items with permission checks
const createNavigation = (permissionChecks: {
  canAccessDashboard: () => boolean;
  canViewInvoices: () => boolean;
  canViewCustomers: () => boolean;
  canAccessVirtualTerminal: () => boolean;
  canViewTransactions: () => boolean;
  canViewProducts: () => boolean;
  canAccessSettings: () => boolean;
  canViewDonations?: () => boolean;
}, user: User | null, companySettings: CompanySettings | null): NavigationItem[] => {
  // First create an array of common menu items
  const menuItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/app',
      icon: LayoutDashboard,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Dashboard is always visible
      permissionCheck: () => true
    },
    {
      name: 'Invoices',
      href: '/app/invoices',
      icon: FileText,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Admin and super_admin always have access, otherwise check permissions
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewInvoices()
    },
    {
      name: 'Customers',
      href: '/app/customers',
      icon: Users,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Admin and super_admin always have access, otherwise check permissions
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewCustomers()
    },
    {
      name: 'Virtual Terminal',
      href: '/app/virtual-terminal',
      icon: CreditCard,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Virtual Terminal is only visible when explicitly enabled in company features
      permissionCheck: () => {
        // Super admin always has access
        if (user?.role === 'super_admin') return true;
        // For other users, check if the feature is enabled for their company
        return companySettings?.features?.virtualTerminal === true && permissionChecks.canAccessVirtualTerminal();
      }
    },
    {
      name: 'Transactions',
      href: '/app/transactions',
      icon: Receipt,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Admin and super_admin always have access, otherwise check permissions
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewTransactions()
    },
    {
      name: 'Products & Services',
      href: '/app/products',
      icon: Package,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Admin and super_admin always have access, otherwise check permissions
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewProducts()
    },
    {
      name: 'Webform Payments',
      href: '/app/donations',
      icon: FileSpreadsheet,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      permissionCheck: () => {
        // Super admin always has access
        if (user?.role === 'super_admin') return true;
        // For other users, check if the feature is enabled for their company
        return companySettings?.features?.donations === true && permissionChecks.canViewDonations?.();
      }
    },
    {
      name: 'Settings',
      href: '/app/settings',
      icon: Settings,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Admin and super_admin always have access, otherwise check permissions
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canAccessSettings()
    },
  ];
  
  // Add Tenants menu item for super_admin users only
  if (user?.role === 'super_admin') {
    menuItems.push({
      name: 'Tenants',
      href: '/app/tenants',
      icon: Building2,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      // Only super_admin can access tenants
      permissionCheck: () => user?.role === 'super_admin',
      subItems: [
        { 
          name: 'Dashboard', 
          href: '/app/tenants', 
          icon: LayoutDashboard 
        },
        {
          name: 'Customers',
          href: '/app/tenants/customers',
          icon: Users
        },
        {
          name: 'Logs', 
          href: '/app/tenants/logs', 
          icon: ScrollText
        },
        { 
          name: 'Merchant Applications', 
          href: '/app/merchant-applications', 
          icon: ClipboardList
        },
        {
          name: 'Banking Partners',
          href: '/app/banking-partners',
          icon: Building2
        },
        {
          name: 'Config',
          href: '/app/tenants/config',
          icon: Settings
        },
      ]
    });
  }
  
  return menuItems;
};

export default function Layout() {
  const location = useLocation();
  const { user } = useAuth();
  const permissions = usePermissions();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenuItem, setExpandedMenuItem] = useState<string | null>(null);
  const { isTestMode } = useTestMode();
  const brandingConfig = useMemo(() => getBrandingConfig(), []);

  // Get impersonation state to determine effective company
  const { isImpersonating, targetUser } = useImpersonation();

  // Use impersonated company ID when impersonating, otherwise use actual user's company
  const effectiveCompanyId = useMemo(() => {
    if (isImpersonating && targetUser?.companyId) {
      return targetUser.companyId;
    }
    return user?.companyId;
  }, [isImpersonating, targetUser?.companyId, user?.companyId]);

  const greetingName = useMemo(() => {
    if (user?.firstName) return user.firstName;
    if (user?.displayName) return user.displayName.split(' ')[0];
    if (user?.email) return user.email.split('@')[0];
    return 'there';
  }, [user?.displayName, user?.email, user?.firstName]);

  const formattedDate = useMemo(() => format(new Date(), 'MMM d, yyyy'), []);
  const { data: settings } = useQuery({
    queryKey: ['companySettings', effectiveCompanyId],
    queryFn: () => getCompanySettings(effectiveCompanyId as string),
    enabled: !!effectiveCompanyId
  });

  // Create navigation items with permission checks
  const baseNavigation = useMemo(
    () => createNavigation(permissions, user, settings),
    [permissions, user, settings]
  );

  // Filter navigation items based on permissions
  const navigation = useMemo(() => {
    return baseNavigation.filter(item => !item.permissionCheck || item.permissionCheck());
  }, [baseNavigation]);

  const normalizedPath = useMemo(() => {
    const path = location?.pathname ?? '/app';

    if (!path || path === '/') {
      return '/app';
    }
    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }
    return path;
  }, [location?.pathname]);

  // Extract applicationId from merchant-applications detail route
  const applicationIdFromRoute = useMemo(() => {
    const match = normalizedPath.match(/^\/app\/merchant-applications\/(.+)$/);
    return match ? match[1] : null;
  }, [normalizedPath]);

  // Fetch merchant application to get business name
  const { data: merchantApplication } = useQuery({
    queryKey: ['merchantApplication', applicationIdFromRoute],
    queryFn: () => MerchantApplicationService.getApplication(applicationIdFromRoute!),
    enabled: !!applicationIdFromRoute,
  });

  // Fetch banking partners for the header badges
  const { data: bankingPartners = [] } = useQuery({
    queryKey: ['banking-partners'],
    queryFn: () => bankingPartnerService.getAllBankingPartners(),
    enabled: !!applicationIdFromRoute,
    staleTime: 5 * 60 * 1000,
  });

  const businessName = useMemo(() => {
    if (merchantApplication?.data) {
      return (merchantApplication.data as any)?.corporationInfo?.businessName ||
             (merchantApplication.data as any)?.businessName;
    }
    return null;
  }, [merchantApplication?.data]);

const activeNavMeta = useMemo(() => {
    let matchedTitle: string | undefined;
    let matchedColor: string | undefined;
    let parentMatchTitle: string | undefined;

    navigation.forEach(item => {
      if (item.href === normalizedPath) {
        matchedTitle = item.name;
        matchedColor = matchedColor ?? item.color;
      } else if (item.href !== '/app' && normalizedPath.startsWith(item.href)) {
        matchedColor = matchedColor ?? item.color;
        parentMatchTitle = parentMatchTitle ?? item.name;
      }

      if (item.subItems) {
        const matchedSub = item.subItems.find(sub => sub.href === normalizedPath);
        if (matchedSub) {
          matchedTitle = matchedSub.name;
          matchedColor = item.color;
        }
      }
    });

    if (!matchedTitle) {
      const segments = normalizedPath.split('/').filter(Boolean);
      if (segments.length) {
        const lastSegment = segments[segments.length - 1];
        const formattedSegment = lastSegment
          .split('-')
          .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' ');
        matchedTitle = parentMatchTitle
          ? `${parentMatchTitle} - ${formattedSegment}`
          : formattedSegment;
      } else {
        matchedTitle = 'Dashboard';
      }
    }

    if (!matchedColor) {
      matchedColor = navigation.find(item => item.href === '/app')?.color;
    }

    return {
      title: matchedTitle,
      color: matchedColor,
    };
}, [navigation, normalizedPath]);

const headerTitle = useMemo(() => {
    const routeOverrideTitles: Record<string, string> = {
      '/app/merchant-applications': 'Payment Activation',
    };

    // If we're on a merchant application detail page with a business name, use it
    if (applicationIdFromRoute && businessName) {
      return businessName;
    }

    if (routeOverrideTitles[normalizedPath]) {
      return normalizedPath === '/app'
        ? `Welcome Back ${greetingName} - Business Dashboard`
        : routeOverrideTitles[normalizedPath];
    }

    if (normalizedPath === '/app') {
      return `Welcome Back ${greetingName} - Business Dashboard`;
    }
    return activeNavMeta.title;
  }, [activeNavMeta.title, greetingName, normalizedPath, applicationIdFromRoute, businessName]);

  const headerTitleClassName = useMemo(() => {
    if (activeNavMeta.color) {
      return cn(
        'text-2xl font-semibold bg-gradient-to-r text-transparent bg-clip-text',
        activeNavMeta.color
      );
    }
    return 'text-2xl font-semibold text-slate-900';
  }, [activeNavMeta.color]);

  // Update page branding based on domain
  useEffect(() => {
    updatePageBranding();
  }, []);

  const handleLogout = () => {
    // Check if user is authenticated via Keycloak SSO
    if (isAuthenticated()) {
      // Use ScalePlus logout which clears all sessions (ScalePlus + Keycloak)
      keycloakLogout();
    } else {
      // User is authenticated via Firebase, use Firebase logout
      auth.signOut();
    }
  };

  return (
    <div
      className={cn(
        "relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100",
        isTestMode && "pt-10"
      )}
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-20 top-[-10%] h-80 w-80 rounded-full bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.18),transparent_65%)] blur-3xl animate-[floatSlow_18s_ease-in-out_infinite]" />
        <div className="absolute bottom-[-15%] right-[-10%] h-96 w-96 rounded-full bg-[radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.16),transparent_60%)] blur-3xl animate-[floatSlow_22s_ease-in-out_infinite]" />
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-[rgba(148,163,184,0.25)] to-transparent" />
      </div>
      {isTestMode && <TestModeBanner />}
      <ImpersonationBanner />
      <div className="relative flex h-screen">
        {/* Sidebar */}
        <div className="relative">
          <div className={cn(
            "hidden md:flex md:flex-col border-r border-white/50 bg-white/80 shadow-xl shadow-slate-900/10 backdrop-blur-2xl transition-all duration-500 ease-in-out h-screen overflow-hidden",
            isCollapsed ? "md:w-24" : "md:w-80"
          )}>
            {/* Background Pattern - Only visible on hover */}
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <BackgroundPattern pattern="villa" opacity={0.04} color="hsl(var(--villavie-green))" />
            </div>
            
            <div className="flex flex-col h-full relative z-10 group">
              {/* Top Section with Logo and Company Name */}
              <div className={cn(
                "flex flex-col items-center gap-3 py-8 border-b border-white/50 bg-gradient-to-b from-white/95 to-white/70",
                isCollapsed ? "px-4" : "px-6"
              )}>
                {/* Logo Container */}
                <div className={cn(
                  "relative group rounded-2xl overflow-hidden transition-all duration-500 bg-white shadow-lg hover:shadow-xl logo-container",
                  isCollapsed ? "w-14 h-14" : "w-36 h-28 mb-5"
                )}>
                  {settings?.logo ? (
                    // Always show customer's logo if available
                    <img
                      src={settings.logo}
                      alt="Company logo"
                      className="w-full h-full object-contain p-3"
                    />
                  ) : (
                    // Default logo using the standard gradient for all domains
                    <div 
                      className={cn(
                        "w-full h-full flex items-center justify-center font-semibold tracking-tight bg-gradient-to-br from-[hsl(var(--villavie-green))] via-[hsl(var(--villavie-blue))] to-[hsl(var(--villavie-gold))] text-white rounded-2xl",
                        isCollapsed ? "text-2xl" : "text-4xl"
                      )}
                    >
                      {settings?.name?.charAt(0) || (brandingConfig.isInvoicerGG ? 'M' : 'W')}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Company Name */}
                {!isCollapsed && (
                  <div className="text-center w-full">
                    <h1 className="text-xl font-semibold bg-gradient-to-r from-[hsl(var(--villavie-green))] via-[hsl(var(--villavie-blue))] to-[hsl(var(--villavie-teal))] bg-clip-text text-transparent truncate">
                      {settings?.name || (brandingConfig.isInvoicerGG ? 'Monarch Labs, LLC' : 'WikipayIt')}
                    </h1>
                  </div>
                )}
              </div>

              {/* Navigation Section */}
              <nav className="flex-1 overflow-y-auto py-6 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
                <div className={cn(
                  "space-y-3",
                  isCollapsed ? "px-3" : "px-4"
                )}>
                  {navigation.map((item) => {
                    const isActive = 
                      location.pathname === item.href || 
                      (item.subItems && item.subItems.some(sub => location.pathname === sub.href));
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isExpanded = expandedMenuItem === item.name;
                    
                    return (
                      <div
                        key={item.name}
                        className="group/item"
                      >
                        {hasSubItems ? (
                          <div>
                            <button
                              onClick={() => setExpandedMenuItem(isExpanded ? null : item.name)}
                              className={cn(
                                "w-full flex items-center gap-x-4 px-4 py-3 rounded-2xl transition-colors",
                                isCollapsed ? "justify-center" : "",
                                isActive || isExpanded
                                  ? "bg-primary text-white"
                                  : "text-slate-600 hover:bg-white/80 hover:text-slate-900",
                              )}
                              title={isCollapsed ? item.name : undefined}
                            >
                              <item.icon
                                className={cn(
                                  "flex-shrink-0",
                                  isCollapsed ? "h-6 w-6" : "h-5 w-5",
                                  isActive || isExpanded ? "text-white" : "text-slate-500 group-hover/item:text-slate-600"
                                )}
                              />
                              {!isCollapsed && (
                                <>
                                  <span className="text-sm font-medium flex-1">{item.name}</span>
                                  <ChevronLeft 
                                    className={cn(
                                      "h-4 w-4 transition-transform duration-300",
                                      isExpanded ? "rotate-90" : "-rotate-0"
                                    )} 
                                  />
                                </>
                              )}
                            </button>
                            
                            {/* Subitems */}
                            {!isCollapsed && isExpanded && item.subItems && (
                              <div className="mt-2 space-y-2">
                                {item.subItems.map((subItem) => {
                                  const isSubActive = location.pathname === subItem.href;
                                  return (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.href}
                                      className={cn(
                                        "flex items-center gap-x-3 px-4 py-2.5 rounded-2xl transition-colors group/subitem",
                                        isSubActive
                                          ? "bg-white text-primary"
                                          : "text-slate-500 hover:bg-white/80 hover:text-slate-900",
                                      )}
                                    >
                                      <subItem.icon className="h-4 w-4" />
                                      <span className="text-sm font-medium">{subItem.name}</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            to={item.href}
                            className={cn(
                              "flex items-center gap-x-4 px-4 py-3.5 rounded-2xl transition-colors group/link",
                              isCollapsed ? "justify-center" : "",
                              isActive
                                ? "bg-primary text-white"
                                : "text-slate-600 hover:bg-white/80 hover:text-slate-900",
                            )}
                            title={isCollapsed ? item.name : undefined}
                          >
                            <div className="relative">
                              <item.icon
                                className={cn(
                                  "flex-shrink-0",
                                  isCollapsed ? "h-6 w-6" : "h-5 w-5",
                                  isActive ? "text-white" : "text-slate-500 group-hover/link:text-slate-600"
                                )}
                              />
                              {item.notificationCount && (
                                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white">
                                  {item.notificationCount}
                                </span>
                              )}
                            </div>
                            {!isCollapsed && (
                              <div className="flex flex-1 items-center justify-between">
                                <span className="text-sm font-medium">{item.name}</span>
                                {item.notificationCount && (
                                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-medium text-red-600">
                                    {item.notificationCount}
                                  </span>
                                )}
                              </div>
                            )}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              </nav>

              {/* Footer Section */}
              <div className={cn(
                "border-t border-white/60 p-4 bg-white/50",
                isCollapsed ? "px-3" : "px-4"
              )}>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full gap-x-4 py-3.5 rounded-2xl hover:bg-red-500/10 hover:text-red-600 group/logout",
                    isCollapsed ? "justify-center px-2" : "justify-start px-4",
                  )}
                  onClick={handleLogout}
                >
                  <LogOut className={cn(
                    "flex-shrink-0",
                    isCollapsed ? "h-6 w-6" : "h-5 w-5"
                  )} />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">Logout</span>
                  )}
                </Button>
                
                {!isCollapsed && (
                  <div className="pb-6 px-2">
                    <div className="mt-4 text-center text-xs text-slate-500">
                      <p className="mt-1">Updated: {new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Floating Collapse Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={cn(
              "absolute -right-4 top-1/2 -translate-y-1/2 -translate-x-0",
              "h-8 w-8 rounded-full p-0",
              "bg-white/80 shadow-lg shadow-slate-900/10 border border-white/60 backdrop-blur",
              "hover:bg-white hover:scale-110 hover:shadow-2xl",
              "transition-all duration-300",
              "flex items-center justify-center",
              "group",
              "cursor-pointer"
            )}
          >
            <ChevronLeft
              className={cn(
                "h-4 w-4 text-slate-500 transition-transform duration-200 group-hover:text-slate-900 pointer-events-none",
                isCollapsed ? "rotate-180" : ""
              )}
            />
          </Button>
        </div>

        {/* Main content */}
        <div className="relative flex flex-1 flex-col overflow-hidden">
          <header className="sticky top-0 z-20 border-b border-white/60 bg-white/70 px-6 py-5 backdrop-blur-xl shadow-sm shadow-slate-900/5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <h2 className={headerTitleClassName}>
                    {applicationIdFromRoute && merchantApplication?.companyId ? (
                      <Link
                        to={`/app/tenants/${merchantApplication.companyId}`}
                        className="hover:text-blue-600 transition-colors"
                      >
                        {headerTitle}
                      </Link>
                    ) : (
                      headerTitle
                    )}
                  </h2>
                  {applicationIdFromRoute && merchantApplication && (
                    <>
                      <Badge
                        className="text-white px-3 py-0.5 text-xs font-normal"
                        style={{ 
                          background: PIPELINE_COLORS[merchantApplication.pipelineStatus] || '#6B7280',
                        }}
                      >
                        {merchantApplication.pipelineStatus.charAt(0).toUpperCase() + merchantApplication.pipelineStatus.slice(1)}
                      </Badge>
                      {merchantApplication.assignedBanks?.map((bankId: string) => {
                        const bank = bankingPartners.find((p: any) => p.id === bankId);
                        if (!bank) return null;
                        return (
                          <Badge
                            key={bankId}
                            className="px-3 py-0.5 text-xs font-normal text-white"
                            style={{ 
                              background: bank.color,
                            }}
                          >
                            {bank.name}
                          </Badge>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 md:gap-4">
                <div className="hidden md:flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1.5 text-sm text-slate-500 shadow-sm shadow-slate-900/5 backdrop-blur">
                  <CalendarDays className="h-4 w-4 text-slate-400" />
                  <span>{formattedDate}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full border border-white/60 bg-white/80 shadow-sm shadow-slate-900/10 hover:bg-white"
                >
                  <Bell className="h-4 w-4 text-slate-500" />
                </Button>
                <div className="hidden sm:flex items-center gap-2 rounded-full border border-white/60 bg-white/80 px-3 py-1.5 shadow-sm shadow-slate-900/5">
                  <UserRound className="h-5 w-5 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600">
                    {greetingName}
                  </span>
                </div>
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-y-auto px-6 pb-10 pt-8 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
            <div className={cn(
              "mx-auto w-full animate-in fade-in-50 duration-500",
              normalizedPath === '/app/merchant-applications' ? '' : 'max-w-7xl'
            )}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      
      {/* Add Toaster component for notifications */}
      <Toaster />
    </div>
  );
}
